import {eq, lt, find, filter, replace} from "lodash";
import type {Blocker, History, Transition} from "history";
import {
	IMAGES_URL,
	ISubNavigationItem,
	PROJECT_NAME,
	PROJECTS_NAVIGATION_ITEMS,
} from "data/constants";
import {type ContextType, useCallback, useContext, useEffect, useState} from "react";
import {
	type Navigator as BaseNavigator,
	UNSAFE_NavigationContext as NavigationContext,
	useNavigate,
} from "react-router-dom";
import emptyPlayer from "assets/img/empty-player.png";
import dayjs from "dayjs";

export const getOpenState = (value: boolean) => (value ? "open" : "closed");

export const getDotClassName = (step: number, currentStep: number) => {
	if (eq(step, currentStep)) {
		return "current";
	}

	if (lt(step, currentStep)) {
		return "past";
	}

	return "";
};

export const getPlayerFlag = (country: string) => {
	return `${IMAGES_URL}flags/${country}.png`;
};

export const getPlayerHeadshot = (playerId: number) => {
	return playerId ? `${IMAGES_URL}players/${playerId}.png` : emptyPlayer;
};

export const getProjectNavigation = () => {
	const activeProject = find(
		PROJECTS_NAVIGATION_ITEMS,
		({projectName}) => projectName === PROJECT_NAME
	);
	const filteredProjects = filter(
		PROJECTS_NAVIGATION_ITEMS.filter((it) => !it.hidden),
		({projectName}) => projectName !== PROJECT_NAME
	);

	return {
		name: activeProject?.name,
		to: "/",
		subItems: filteredProjects,
	} as ISubNavigationItem;
};

interface INavigator extends BaseNavigator {
	block: History["block"];
}

type NavigationContextWithBlock = ContextType<typeof NavigationContext> & {navigator: INavigator};

/**
 * @source https://github.com/remix-run/react-router/commit/256cad70d3fd4500b1abcfea66f3ee622fb90874
 */
const useBlocker = (blocker: Blocker, when = true) => {
	const {navigator} = useContext(NavigationContext) as NavigationContextWithBlock;

	useEffect(() => {
		if (!when) {
			return;
		}

		const unblock = navigator.block((tx: Transition) => {
			const autoUnblockingTx = {
				...tx,
				retry() {
					// Automatically unblock the transition so it can play all the way
					// through before retrying it. TODO: Figure out how to re-enable
					// this block if the transition is cancelled for some reason.
					unblock();
					tx.retry();
				},
			};

			blocker(autoUnblockingTx);
		});

		return unblock;
	}, [navigator, blocker, when]);
};

export const useBlockNavigation = (when: boolean) => {
	const navigate = useNavigate();
	const [showPrompt, setShowPrompt] = useState(false);
	const [lastLocation, setLastLocation] = useState<Transition | null>(null);
	const [confirmedNavigation, setConfirmedNavigation] = useState(false);

	const cancelNavigation = useCallback(() => {
		setShowPrompt(false);
	}, []);

	const handleBlockedNavigation: Blocker = useCallback(
		(nextLocation) => {
			const isRouteChanged = nextLocation.location.pathname !== window.location.pathname;

			if (!confirmedNavigation && isRouteChanged) {
				setShowPrompt(true);
				setLastLocation(nextLocation);

				return false;
			}

			return true;
		},
		[confirmedNavigation]
	);

	const confirmNavigation = useCallback(() => {
		setShowPrompt(false);
		setConfirmedNavigation(true);
	}, []);

	useEffect(() => {
		if (confirmedNavigation && lastLocation) {
			if (lastLocation.location.pathname.includes("your-picks/")) {
				navigate(replace(lastLocation.location.pathname, "your-picks/", ""));
				return;
			}
			navigate(lastLocation.location.pathname);
		}
	}, [confirmedNavigation, lastLocation, navigate]);

	useBlocker(handleBlockedNavigation, when);

	return [showPrompt, confirmNavigation, cancelNavigation] as const;
};

export const minDate = dayjs("1900-01-01");
